import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PaletteIcon from '@mui/icons-material/Palette';
import { useNavigate } from 'react-router-dom';
import designSystemService from '../../services/designSystemService';

// ── Color swatches ─────────────────────────────────────────────────────────────

const ColorSwatches = ({ colors = {} }) => {
  const shown = Object.entries(colors)
    .filter(([, v]) => v)
    .slice(0, 6);

  if (!shown.length) return null;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
      {shown.map(([name, val]) => (
        <Tooltip key={name} title={`${name}: ${val}`} placement="top">
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: val,
              border: '1.5px solid rgba(0,0,0,0.08)',
              flexShrink: 0,
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

// ── Design System Card ─────────────────────────────────────────────────────────

const DSCard = ({ ds, onEdit, onDelete }) => {
  const colors = ds.tokens?.colors || {};
  const fontFamily = ds.tokens?.typography?.fontFamily || '';
  const dateStr = ds.updatedAt
    ? new Date(ds.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #eee',
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s',
        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
      }}
    >
      {/* Color strip */}
      <Box sx={{ height: 8, display: 'flex' }}>
        {Object.values(colors).filter(Boolean).slice(0, 5).map((c, i) => (
          <Box key={i} sx={{ flex: 1, background: c }} />
        ))}
        {Object.values(colors).filter(Boolean).length === 0 && (
          <Box sx={{ flex: 1, background: 'linear-gradient(90deg, #e0e0e0, #f5f5f5)' }} />
        )}
      </Box>

      <CardActionArea onClick={() => onEdit(ds.id)}>
        <CardContent sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
            {ds.name}
          </Typography>
          {ds.company && (
            <Typography
              variant="caption"
              sx={{
                color: '#888',
                fontSize: 12,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {ds.company}
            </Typography>
          )}
          <ColorSwatches colors={colors} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            {fontFamily && (
              <Chip
                label={fontFamily}
                size="small"
                sx={{ fontSize: 10, height: 18, background: '#f5f5f5', fontFamily: fontFamily }}
              />
            )}
            <Typography variant="caption" sx={{ color: '#bbb', fontSize: 11, ml: 'auto' }}>
              {dateStr}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>

      <Box
        sx={{
          px: 1.5,
          pb: 1.5,
          display: 'flex',
          gap: 0.5,
          borderTop: '1px solid #f5f5f5',
          pt: 1,
        }}
      >
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(ds.id)} sx={{ color: '#666' }}>
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => onDelete(ds.id)}
            sx={{ color: '#ccc', '&:hover': { color: '#e53e3e' } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Card>
  );
};

// ── DesignSystemBrowser ────────────────────────────────────────────────────────

const DesignSystemBrowser = () => {
  const navigate = useNavigate();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await designSystemService.listDesignSystems();
        setSystems(data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDelete = async (id) => {
    await designSystemService.deleteDesignSystem(id);
    setSystems((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', background: '#fff' }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 4, py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #e85d26, #f97316)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaletteIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <div>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 22, color: '#1a1a1a', lineHeight: 1.2 }}>
                Design Systems
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: 13 }}>
                Brand tokens and assets for AI-guided generation
              </Typography>
            </div>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/design-studio/design-systems/new')}
            sx={{
              background: '#e85d26',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              '&:hover': { background: '#cf4f1e' },
            }}
          >
            New design system
          </Button>
        </Box>

        <Grid container spacing={2}>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                </Grid>
              ))
            : systems.map((ds) => (
                <Grid item xs={12} sm={6} md={4} key={ds.id}>
                  <DSCard
                    ds={ds}
                    onEdit={(id) => navigate(`/design-studio/design-systems/${id}`)}
                    onDelete={handleDelete}
                  />
                </Grid>
              ))}
        </Grid>

        {!loading && systems.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              border: '2px dashed #eee',
              borderRadius: 3,
              mt: 2,
            }}
          >
            <PaletteIcon sx={{ fontSize: 40, color: '#ddd', mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
              No design systems yet
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate('/design-studio/design-systems/new')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Set up your first design system
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DesignSystemBrowser;
